/** MasterclassComments — a discussion thread on a masterclass. Reuses the
 *  community report flow (UGC moderation) + blocklist filtering. */
import React, { useEffect, useState } from "react";
import { Send, Trash2, MoreHorizontal, Flag, Ban } from "lucide-react";
import { GOLD } from "../constants/theme";
import { fetchComments, postComment, deleteComment, getMyUserId, commentAuthorName, commentInitials } from "./masterclassCommentsApi";
import { reportContent } from "../utils/communityApi";
import { isBlocked, blockUser } from "../community/blocklist";
import { haptic } from "../../utils/nativeBridge";
import BamLoading from "../shared/BamLoading";

function fmtWhen(iso) {
  try {
    const d = new Date(iso), now = Date.now(), diff = (now - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
}

function Avatar({ author, C }) {
  const url = author?.avatar_url;
  if (url) return <img src={url} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} onError={(e) => { e.currentTarget.style.display = "none"; }} />;
  return <div style={{ width: 34, height: 34, borderRadius: "50%", flexShrink: 0, background: `${GOLD}22`, color: GOLD, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, fontWeight: 700 }}>{commentInitials(author)}</div>;
}

export default function MasterclassComments({ moduleKey, C, dark, title = "Discussion", prompt = "Questions, takeaways, how you ran it with your team." }) {
  const [me, setMe] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [menuFor, setMenuFor] = useState(null);

  const refresh = async () => {
    const list = await fetchComments(moduleKey);
    setComments(list.filter((c) => !isBlocked(c.author_id)));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const uid = await getMyUserId(); if (!cancelled) setMe(uid);
        const list = await fetchComments(moduleKey);
        if (!cancelled) setComments(list.filter((c) => !isBlocked(c.author_id)));
      } catch (e) { console.error("[mc comments] load", e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [moduleKey]);

  const submit = async () => {
    const body = draft.trim();
    if (!body || posting) return;
    setPosting(true); haptic("light");
    try { await postComment(moduleKey, body); setDraft(""); await refresh(); }
    catch (e) { console.error("[mc comments] post", e); }
    finally { setPosting(false); }
  };
  const remove = async (c) => {
    setMenuFor(null);
    setComments((prev) => prev.filter((x) => x.id !== c.id));
    try { await deleteComment(c.id); } catch (e) { console.error(e); }
  };
  const report = async (c) => {
    setMenuFor(null);
    try { await reportContent({ contentType: "masterclass_comment", contentId: c.id }); } catch (e) { console.error(e); }
  };
  const block = (c) => {
    setMenuFor(null);
    blockUser(c.author_id);
    setComments((prev) => prev.filter((x) => x.author_id !== c.author_id));
  };

  return (
    <div style={{ marginTop: 40, paddingTop: 28, borderTop: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: C.text, letterSpacing: 0.3, marginBottom: 4 }}>
        {title} {comments.length > 0 && <span style={{ color: C.textDim, fontWeight: 600 }}>· {comments.length}</span>}
      </div>
      <div style={{ fontSize: 12.5, color: C.textDim, marginBottom: 16 }}>{prompt}</div>

      {/* Composer */}
      <div style={{ display: "flex", gap: 10, marginBottom: 22 }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          placeholder="Add to the discussion…"
          rows={2}
          style={{ flex: 1, resize: "vertical", minHeight: 44, maxHeight: 160, padding: "10px 12px", borderRadius: 12, background: dark ? "rgba(255,255,255,0.04)" : "#fff", border: `1px solid ${C.border}`, color: C.text, fontSize: 15, fontFamily: "'DM Sans', sans-serif", outline: "none" }}
        />
        <button onClick={submit} disabled={!draft.trim() || posting}
          style={{ alignSelf: "flex-end", background: draft.trim() ? GOLD : C.border, color: draft.trim() ? "#111" : C.textDim, border: "none", borderRadius: 12, padding: "0 16px", height: 44, fontWeight: 800, fontSize: 13, cursor: draft.trim() ? "pointer" : "default", display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
          <Send size={15} /> Post
        </button>
      </div>

      {loading && <BamLoading pad={12}/>}
      {!loading && comments.length === 0 && (
        <div style={{ color: C.textDim, fontSize: 13.5, padding: "8px 0" }}>No comments yet. Be the first to add one.</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {comments.map((c) => {
          const mine = c.author_id === me;
          return (
            <div key={c.id} style={{ display: "flex", gap: 11, position: "relative" }}>
              <Avatar author={c.author} C={C} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: C.text }}>{commentAuthorName(c.author)}</span>
                  <span style={{ fontSize: 11.5, color: C.textDim }}>{fmtWhen(c.created_at)}</span>
                  <button onClick={() => setMenuFor(menuFor === c.id ? null : c.id)} style={{ marginLeft: "auto", background: "none", border: "none", color: C.textDim, cursor: "pointer", padding: 2, display: "flex" }} aria-label="More"><MoreHorizontal size={16} /></button>
                </div>
                <div style={{ fontSize: 14.5, color: C.textMid, lineHeight: 1.5, marginTop: 3, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{c.content}</div>
                {menuFor === c.id && (
                  <div style={{ position: "absolute", right: 0, top: 22, background: dark ? "#1a1a1a" : "#fff", border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", zIndex: 20, minWidth: 130, boxShadow: "0 8px 24px rgba(0,0,0,0.3)" }}>
                    {mine ? (
                      <MenuItem icon={<Trash2 size={14} />} label="Delete" danger onClick={() => remove(c)} C={C} />
                    ) : (
                      <>
                        <MenuItem icon={<Flag size={14} />} label="Report" onClick={() => report(c)} C={C} />
                        <MenuItem icon={<Ban size={14} />} label="Block user" onClick={() => block(c)} C={C} />
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MenuItem({ icon, label, danger, onClick, C }) {
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 9, padding: "9px 14px", background: "none", border: "none", cursor: "pointer", color: danger ? "#f87171" : C.textMid, fontSize: 13.5, width: "100%", textAlign: "left", fontFamily: "'DM Sans', sans-serif" }}>
      {icon}{label}
    </button>
  );
}
